import { Client as SearchClient, UnifiedSearchResultModel } from '@bizzkit/ecommerce-search/client';
import { keepPreviousData, useQuery, UseQueryResult } from '@tanstack/react-query';
import { queryKeys } from '@/api/apiConfig';
import { mapQuickSearchResult } from '@/api/product/mapping';
import { QuickSearchResult } from '@/api/product/types';
import { ProblemDetails, SearchSettingsResponse } from '@/generated/data.schemas';

interface FetchQuickSearchParams {
	readonly searchApiHost: string;
	readonly searchToken: string;
	readonly searchSegment: string;
	readonly quickSearchScopeId: string;
	readonly phrase: string;
}

const fetchQuickSearch = async (params: FetchQuickSearchParams): Promise<UnifiedSearchResultModel> => {
	const searchClient = new SearchClient({ baseUrl: params.searchApiHost });
	const response = await searchClient.search.search({
		segmentId: params.searchSegment,
		scopeId: params.quickSearchScopeId,
		phrase: params.phrase,
		authenticationToken: params.searchToken,
	});
	return response.data;
};

export const useQuickSearchQuery = (
	phrase: string,
	isActive: boolean,
	settings?: SearchSettingsResponse,
	isSalesPortal?: boolean,
): UseQueryResult<QuickSearchResult, undefined | ProblemDetails> => {
	const params: FetchQuickSearchParams = {
		searchApiHost: settings?.searchApiHost ?? '',
		quickSearchScopeId: isSalesPortal
			? (settings?.quickSearchScopeId ?? '')
			: (settings?.contentSearchScopeId ?? ''),
		searchSegment: settings?.searchSegment ?? '',
		searchToken: settings?.searchToken ?? '',
		phrase,
	};

	return useQuery({
		queryKey: queryKeys.product.quickSearch(params).queryKey,
		queryFn: () => fetchQuickSearch(params),
		placeholderData: keepPreviousData,
		// This query will not execute until settings exist
		enabled: !!settings && isActive,
		select: (d) => {
			return mapQuickSearchResult(d, params.phrase, settings);
		},
		retry: false,
	});
};
